import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { TenantModuleKind, TenantSettingsResponse } from '../api/types';
import { useApi } from '../auth/ApiProvider';
import { useSession } from '../auth/SessionProvider';
import { useI18n } from '../i18n/I18nProvider';

/**
 * Loads the acting tenant's settings and applies them to formatting.
 *
 * Timezone, currency and numeral style are per tenant, never global (SRS §2.4, NFR-I18N-03). Until this
 * has run, dates format in `Asia/Damascus` and money in SYP — tenant #1's defaults — which is right for
 * the first customer and wrong for the second, so every tenant-scoped app calls this in its shell.
 *
 * The tenant's own default language is *not* forced onto the user. A parent who has chosen English keeps
 * it; the tenant default decides what someone sees before they have chosen anything, and that is already
 * settled by the browser's stored preference.
 */
export function useTenantSettings(): {
  settings: TenantSettingsResponse | undefined;
  isPending: boolean;
} {
  const api = useApi();
  const { session } = useSession();
  const { applySettings } = useI18n();

  const isTenantScoped = session?.membership.scope === 'Tenant';

  const query = useQuery({
    queryKey: ['tenant', 'current', session?.membership.tenantId],
    queryFn: () => api.currentTenant(),
    enabled: isTenantScoped,
    // These change about once a year. Refetching them on every mount would be a request per screen.
    staleTime: 30 * 60_000,
  });

  const { timeZone, currency, numeralStyle } = query.data ?? {};

  useEffect(() => {
    if (!timeZone || !currency) return;
    applySettings({
      timeZone,
      currency,
      numerals: numeralStyle === 'ArabicIndic' ? 'eastern' : 'western',
    });
  }, [timeZone, currency, numeralStyle, applySettings]);

  return { settings: query.data, isPending: isTenantScoped && query.isPending };
}

/**
 * One of the behaviour settings the API serves this person (NR-1), as `/admin/settings` writes it: `True`/`False`,
 * `HH:mm`. Undefined until the tenant has loaded, and for a setting this person's permissions do not act on.
 */
export function useMemberSetting(key: string): string | undefined {
  return useTenantSettings().settings?.settings?.[key];
}

/** A switch among those settings, false until it is known to be on. */
export function useMemberSwitch(key: string): boolean {
  return useMemberSetting(key)?.toLowerCase() === 'true';
}

/** Whether the nursery has a module on (NR-11): false until the tenant has loaded, and for a module that is off. */
export function useModule(kind: TenantModuleKind): boolean {
  return useTenantSettings().settings?.modules?.includes(kind) ?? false;
}
